import { useEffect, useRef, useState } from 'react';
import type { StainDto } from '../api/companions';
import { petBasePath, rememberMissingPetBase, type PetBaseVariant } from '../compositor/assetPaths';
import { StainOverlay } from '../fx/StainOverlay';
import { gestureForPokeClass } from './gestures';
import { markPet3dUnavailable, usePet3d } from './loader';
import type { Facing, LiveFit, LiveHandle, Motion, Outfit } from './types';

const reduced = () => typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/** The live 3D pet over its 2D PNG (DESIGN.md "3D characters" §4): the PNG (with its stain
 *  overlay) shows until the first 3D frame, then the canvas fades in; on any failure the canvas
 *  goes and the 2D pet stays exactly as the Compositor drew it before. `variant` is the PNG still
 *  and, as eat/joy, the 3D hold. `outfit` dresses the 3D pet (the PNG stays undressed: there is
 *  no 2D cosmetic art); `yaw` overrides the facing and `sway` adds the chunk's idle turn (the
 *  wardrobe preview); `paused` holds the scene still on top of the hidden/off-screen pause (the
 *  Habitat under the Dress overlay). `frame="fit"` (the wardrobe) frames the pet's own silhouette instead of
 *  the shared lock frame, and the PNG under the canvas takes the same zoom so the cross-fade does not jump. */
export function Pet3DStage(props: {
  species: string; stage: string; variant: PetBaseVariant; alt: string;
  motion: Motion; facing: Facing; stains: StainDto[]; stainOpacity: number;
  pokeClass: string | null; pokeGen: number; className?: string;
  outfit?: Outfit; yaw?: number; sway?: boolean; paused?: boolean; frame?: 'lock' | 'fit';
}) {
  const png = petBasePath(props.species, props.stage, props.variant);
  const hold = props.variant === 'base' ? null : props.variant;
  const mod = usePet3d();
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  // State, not a ref: a new scene (StrictMode's remount, a species/stage change) re-runs the
  // update effect below so it starts from the current wander state, not the scene's defaults.
  const [live, setLive] = useState<LiveHandle | null>(null);
  const [ready, setReady] = useState(false);
  const [failed, setFailed] = useState(false);
  const [fit, setFit] = useState<LiveFit | null>(null);
  const use3d = !!mod && !failed;
  // Refs, read by the mount effect: an outfit change re-dresses through update(), never a remount,
  // and the pause rule below must see the latest `paused` from inside its event handlers.
  const outfitRef = useRef(props.outfit);
  outfitRef.current = props.outfit;
  const pausedRef = useRef(!!props.paused);
  const repause = useRef<() => void>(() => {});

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!use3d || !canvas || !mod) return;
    let handle: LiveHandle;
    try {
      handle = mod.mountLive(canvas, {
        species: props.species, stage: props.stage, outfit: outfitRef.current, frame: props.frame ?? 'lock',
        onLost: () => { setFailed(true); setReady(false); markPet3dUnavailable(); },
      });
    } catch (e) {
      console.warn('pet3d: live scene failed', e);
      setFailed(true);
      return;
    }
    setLive(handle);
    setFit(handle.fit ?? null);
    handle.onFirstFrame(() => setReady(true));
    let onScreen = true;
    const pause = () => handle.setPaused(document.hidden || !onScreen || pausedRef.current);
    repause.current = pause;
    pause();
    const io = typeof IntersectionObserver === 'function'
      ? new IntersectionObserver((entries) => { onScreen = entries[entries.length - 1].isIntersecting; pause(); })  // batched: the last is current
      : null;
    io?.observe(canvas);
    document.addEventListener('visibilitychange', pause);
    return () => {
      repause.current = () => {};
      io?.disconnect();
      document.removeEventListener('visibilitychange', pause);
      handle.dispose();
      setLive(null);
      setFit(null);
      setReady(false);
    };
  }, [use3d, mod, props.species, props.stage, props.frame]);

  useEffect(() => {
    pausedRef.current = !!props.paused;
    repause.current();
  }, [props.paused]);

  useEffect(() => {
    live?.update({
      motion: props.motion, facing: props.facing, hold, reducedMotion: reduced(),
      stains: props.stains, stainOpacity: props.stainOpacity,
      ...(props.outfit !== undefined && { outfit: props.outfit }),
      ...(props.yaw !== undefined && { yaw: props.yaw }),
      ...(props.sway !== undefined && { sway: props.sway }),
    });
  }, [live, props.motion, props.facing, hold, props.stains, props.stainOpacity, props.outfit, props.yaw, props.sway]);

  // A ref, so StrictMode's second effect run (and a new scene) sees the gen as played already.
  const played = useRef(props.pokeGen);
  useEffect(() => {
    if (props.pokeGen === played.current) return;
    played.current = props.pokeGen;                       // a poke with no scene up is dropped, not replayed later
    const g = gestureForPokeClass(props.pokeClass);
    if (g) live?.play(g.gesture, g.ms);
  }, [live, props.pokeGen, props.pokeClass]);

  return (
    <span
      className={['pet3d-stage', ready ? 'is-3d' : undefined, fit ? 'pet3d-fit' : undefined, props.className].filter(Boolean).join(' ')}
      data-stains={props.stains.length}
    >
      <span className="pet3d-flat" style={fit ? { transform: `scale(${fit.zoom}) translateY(${-fit.dy * 100}%)` } : undefined}>
        <img
          className="pet3d-png"
          src={png}
          alt={props.alt}
          onError={props.variant === 'base' ? undefined : (e) => {
            // Compositor's fallback: a missing eat/joy still shows the base PNG, remembered.
            rememberMissingPetBase(props.species, props.stage, props.variant);
            e.currentTarget.src = petBasePath(props.species, props.stage, 'base');
          }}
        />
        <StainOverlay stains={props.stains} opacity={props.stainOpacity} maskUrl={png} />
      </span>
      {use3d && <canvas ref={canvasRef} className="pet3d-canvas" aria-hidden="true" />}
    </span>
  );
}
